import {OfferType} from "../../types/OfferType";
import {isRoomsFilterEncoded} from "../friendly_offer_list/offer_url_builder";
import {
    hasNumericValue,
    IOfferListQueryValue,
    IOfferListUrlInput,
    isTruthyFlag,
    shouldOmitQueryValue,
    toOfferListType,
    toOptionalNumber,
    toValueList
} from "./offer_list_url_values";

/**
 * Location + property type. Never counted as a filter.
 * `house_type` with a single value on a house listing is a category segment (`/blizniaki/`), not a filter.
 */
export const offerListBaseKeys = ["type", "region", "region_type", "country"] as const;

/**
 * One entry = one filter, regardless of how many bounds are set (price_0 + price_1 = 1).
 * Key order here is also the canonical query order.
 */
export const offerListFilterGroups = [
    ["price_0", "price_1"],
    ["area_0", "area_1"],
    ["rooms_0", "rooms_1"],
    ["floor_choices"],
    ["house_storeys"],
    ["house_additional_areas"],
    ["construction_end_date"],
    ["price_lower_than_average"],
    ["is_luxury"],
    ["house_type"]
] as const;

/** Always kept in the query string and never counted toward the 0/1/2+ rule. */
export const offerListNonFilterKeys = ["sort", "distance", "promotions", "page", "pid"] as const;

export const offerListCanonicalKeyOrder: string[] = [...offerListFilterGroups.flat(), ...offerListNonFilterKeys];

// `instalment` (monthly budget) is read by the listing but never counted toward the 0/1/2+ rule.
const offerListQueryKeys = new Set<string>([...offerListBaseKeys, ...offerListCanonicalKeyOrder, "instalment"]);

/** Keys the listing reads itself. Anything else (utm_*, gclid, ...) belongs to someone else and is passed on as sent. */
export const isOfferListQueryKey = (key: string): boolean => offerListQueryKeys.has(key);

/** Empty or "false" values clear a listing key; another parameter keeps them (and a key without a value), only `undefined` or a code-level `false` drops it. */
export const shouldOmitOfferListQueryValue = (key: string, value: IOfferListQueryValue): boolean =>
    isOfferListQueryKey(key) ? shouldOmitQueryValue(value) : value === undefined || value === false;

const isHouseListing = (query: IOfferListUrlInput) => {
    const type = toOfferListType(query.type);

    return type == null || type === OfferType.HOUSE;
};

const houseTypeValues = (query: IOfferListUrlInput): number[] =>
    toValueList(query.house_type)
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isFinite(value));

/** Single house subtype that extends the base category instead of counting as a filter. */
export const getCategoryHouseType = (query: IOfferListUrlInput): number | null => {
    const values = houseTypeValues(query);

    if (!isHouseListing(query) || values.length !== 1) {
        return null;
    }

    return values[0];
};

const isReadyFilter = (query: IOfferListUrlInput) => {
    const values = toValueList(query.construction_end_date);

    return values.length > 0 && values.every((value) => value === "0");
};

const isHouseTypeAttribute = (query: IOfferListUrlInput) => {
    const values = houseTypeValues(query);

    if (values.length === 0 || getCategoryHouseType(query) != null) {
        return false;
    }

    return true;
};

/**
 * How many attribute filters the query carries.
 * A lone house subtype (`/blizniaki/`) is a category and is not included.
 * A full rooms range that the friendly slug would drop (1-5) is not included.
 */
export const countOfferListFilters = (query: IOfferListUrlInput): number => {
    let count = 0;

    if (hasNumericValue(query.price_0) || hasNumericValue(query.price_1)) {
        count += 1;
    }

    if (hasNumericValue(query.area_0) || hasNumericValue(query.area_1)) {
        count += 1;
    }

    if (isRoomsFilterEncoded(toOptionalNumber(query.rooms_0), toOptionalNumber(query.rooms_1))) {
        count += 1;
    }

    if (hasNumericValue(query.floor_choices)) {
        count += 1;
    }

    if (hasNumericValue(query.house_storeys)) {
        count += 1;
    }

    if (hasNumericValue(query.house_additional_areas)) {
        count += 1;
    }

    if (isReadyFilter(query)) {
        count += 1;
    }

    if (isTruthyFlag(query.price_lower_than_average)) {
        count += 1;
    }

    if (isTruthyFlag(query.is_luxury)) {
        count += 1;
    }

    if (isHouseTypeAttribute(query)) {
        count += 1;
    }

    return count;
};

/**
 * Multifilter: two attribute filters, or a house subtype together with at least one attribute filter.
 * Those addresses are query-param URLs and are not indexable.
 */
export const isMultiFilterQuery = (query: IOfferListUrlInput): boolean => {
    const attributeCount = countOfferListFilters(query);

    if (getCategoryHouseType(query) != null && attributeCount >= 1) {
        return true;
    }

    return attributeCount >= 2;
};

/** Whether the URL query itself carries a filter (a saved-search slug can resolve to filters that are not in the URL). */
export const hasOfferListFilterInQuery = (query: IOfferListUrlInput): boolean =>
    offerListFilterGroups.some((group) => group.some((key) => !shouldOmitQueryValue(query[key])));
import {OfferType} from "../../types/OfferType";
import {isRoomsFilterEncoded} from "../friendly_offer_list/offer_url_builder";
import {
    hasNumericValue,
    IOfferListQueryValue,
    IOfferListUrlInput,
    isTruthyFlag,
    shouldOmitQueryValue,
    toOfferListType,
    toOptionalNumber,
    toValueList
} from "./offer_list_url_values";

/**
 * Location + property type. Never counted as a filter.
 * `house_type` with a single value on a house listing is a category segment (`/blizniaki/`), not a filter.
 */
export const offerListBaseKeys = ["type", "region", "region_type", "country"] as const;

/**
 * One entry = one filter, regardless of how many bounds are set (price_0 + price_1 = 1).
 * Key order here is also the canonical query order.
 */
export const offerListFilterGroups = [
    ["price_0", "price_1"],
    ["area_0", "area_1"],
    ["rooms_0", "rooms_1"],
    ["floor_choices"],
    ["house_storeys"],
    ["house_additional_areas"],
    ["construction_end_date"],
    ["price_lower_than_average"],
    ["is_luxury"],
    ["house_type"]
] as const;

/** Always kept in the query string and never counted toward the 0/1/2+ rule. */
export const offerListNonFilterKeys = ["sort", "distance", "promotions", "page", "pid"] as const;

export const offerListCanonicalKeyOrder: string[] = [...offerListFilterGroups.flat(), ...offerListNonFilterKeys];

// `instalment` (monthly budget) is read by the listing but never counted toward the 0/1/2+ rule.
const offerListQueryKeys = new Set<string>([...offerListBaseKeys, ...offerListCanonicalKeyOrder, "instalment"]);

/** Keys the listing reads itself. Anything else (utm_*, gclid, ...) belongs to someone else and is passed on as sent. */
export const isOfferListQueryKey = (key: string): boolean => offerListQueryKeys.has(key);

/** Empty or "false" values clear a listing key; another parameter keeps them (and a key without a value), only `undefined` or a code-level `false` drops it. */
export const shouldOmitOfferListQueryValue = (key: string, value: IOfferListQueryValue): boolean =>
    isOfferListQueryKey(key) ? shouldOmitQueryValue(value) : value === undefined || value === false;

const isHouseListing = (query: IOfferListUrlInput) => {
    const type = toOfferListType(query.type);

    return type == null || type === OfferType.HOUSE;
};

const houseTypeValues = (query: IOfferListUrlInput): number[] =>
    toValueList(query.house_type)
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isFinite(value));

/** Single house subtype that extends the base category instead of counting as a filter. */
export const getCategoryHouseType = (query: IOfferListUrlInput): number | null => {
    const values = houseTypeValues(query);

    if (!isHouseListing(query) || values.length !== 1) {
        return null;
    }

    return values[0];
};

const isReadyFilter = (query: IOfferListUrlInput) => {
    const values = toValueList(query.construction_end_date);

    return values.length > 0 && values.every((value) => value === "0");
};

const isHouseTypeAttribute = (query: IOfferListUrlInput) => {
    const values = houseTypeValues(query);

    if (values.length === 0 || getCategoryHouseType(query) != null) {
        return false;
    }

    return true;
};

/**
 * How many attribute filters the query carries.
 * A lone house subtype (`/blizniaki/`) is a category and is not included.
 * A full rooms range that the friendly slug would drop (1-5) is not included.
 */
export const countOfferListFilters = (query: IOfferListUrlInput): number => {
    let count = 0;

    if (hasNumericValue(query.price_0) || hasNumericValue(query.price_1)) {
        count += 1;
    }

    if (hasNumericValue(query.area_0) || hasNumericValue(query.area_1)) {
        count += 1;
    }

    if (isRoomsFilterEncoded(toOptionalNumber(query.rooms_0), toOptionalNumber(query.rooms_1))) {
        count += 1;
    }

    if (hasNumericValue(query.floor_choices)) {
        count += 1;
    }

    if (hasNumericValue(query.house_storeys)) {
        count += 1;
    }

    if (hasNumericValue(query.house_additional_areas)) {
        count += 1;
    }

    if (isReadyFilter(query)) {
        count += 1;
    }

    if (isTruthyFlag(query.price_lower_than_average)) {
        count += 1;
    }

    if (isTruthyFlag(query.is_luxury)) {
        count += 1;
    }

    if (isHouseTypeAttribute(query)) {
        count += 1;
    }

    return count;
};

/**
 * Multifilter: two attribute filters, or a house subtype together with at least one attribute filter.
 * Those addresses are query-param URLs and are not indexable.
 */
export const isMultiFilterQuery = (query: IOfferListUrlInput): boolean => {
    const attributeCount = countOfferListFilters(query);

    if (getCategoryHouseType(query) != null && attributeCount >= 1) {
        return true;
    }

    return attributeCount >= 2;
};

/** Whether the URL query itself carries a filter (a saved-search slug can resolve to filters that are not in the URL). */
export const hasOfferListFilterInQuery = (query: IOfferListUrlInput): boolean =>
    offerListFilterGroups.some((group) => group.some((key) => !shouldOmitQueryValue(query[key])));
